<template>
    <div class="config-competitor-group">
        <div class="config-competitor-card rounder-8 mb-4">
            <div class="filters-summary-header">
                <span class="filters-summary-icon">
                    <font-awesome-icon :icon="['fas', 'filter']" />
                </span>
                <span class="filters-summary-title">Filtros de productos:</span>
            </div>

            <div class="row g-3 mb-3">
                <div class="col-6" v-if="shouldShowFilter('supplier')">
                    <label class="form-label">{{ getFilterLabel('supplier') }}</label>
                    <v-select
                        :options="proveedores"
                        v-model="localFilters.supplier"
                        :reduce="proveedor => proveedor"
                        :placeholder="`Seleccionar ${getFilterLabel('supplier').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('supplier', $event)"
                    />
                </div>
                <div class="col-6" v-if="shouldShowFilter('brand')">
                    <label class="form-label">{{ getFilterLabel('brand') }}</label>
                    <v-select
                        :options="marcas"
                        v-model="localFilters.brand"
                        :reduce="marca => marca"
                        :placeholder="`Seleccionar ${getFilterLabel('brand').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('brand', $event)"
                    />
                </div>
                <div class="col-6" v-if="shouldShowFilter('select_category')">
                    <label class="form-label">{{ getFilterLabel('select_category') }}</label>
                    <filter-categories @getData="getCategory" class="custom-select" :category="localFilters.select_category"/>
                </div>
                <div class="col-6" v-if="shouldShowFilter('product_group')">
                    <label class="form-label">{{ getFilterLabel('product_group') }}</label>
                    <v-select
                        :options="groups"
                        v-model="localFilters.product_group"
                        :reduce="group => group"
                        :placeholder="`Seleccionar ${getFilterLabel('product_group').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('product_group', $event)"
                    />
                </div>
                <div class="col-6" v-if="shouldShowFilter('product_type')">
                    <label class="form-label">{{ getFilterLabel('product_type') }}</label>
                    <v-select
                        :options="filtros"
                        v-model="localFilters.product_type"
                        :reduce="filtro => filtro"
                        :placeholder="`Seleccionar ${getFilterLabel('product_type').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('product_type', $event)"
                    />
                </div>
            </div>

            <div class="divider-line"></div>

            <div class="active-filters-list" v-if="activeFilters.length > 0">
                <span 
                    v-for="(filter, index) in activeFilters" 
                    :key="index"
                    class="filter-chip"
                >
                    <span class="filter-chip-label">{{ filter.label }}</span>
                    <span class="filter-chip-value">{{ filter.value }}</span>
                </span>
            </div>
            <div v-else class="no-filters-msg">
                <span class="text-secondary size-13">Se deben aplicar filtros</span>
            </div>

            <div class="divider-line"></div>

            <!-- 4 tarjetas de posicionamiento -->
            <div class="position-cards-grid mb-4">
                <div 
                    v-for="option in positioningOptions" 
                    :key="option.value"
                    class="position-card"
                    :class="{ 'position-card--active': positioning === option.value }"
                    @click="positioning = option.value"
                >
                    <div class="position-card-icon">
                        <font-awesome-icon :icon="['fas', option.icon]" />
                    </div>
                    <div class="position-card-title">{{ option.title }}</div>
                    <div class="position-card-desc">{{ option.desc }}</div>
                </div>
            </div>

            <!-- Diferencial (oculto para Paridad y Precio fijo) -->
            <div class="mb-4 w-100" v-if="positioning != 1 && positioning != 3">
                <label class="form-label fw-600 size-13 text-secondary mb-2">DIFERENCIAL FRENTE AL PRECIO DE MERCADO</label>
                <div class="input-group">
                    <input 
                        type="number" 
                        class="form-control fw-600 size-16" 
                        v-model.number="targetDifferential"
                        placeholder="0.000"
                        step="0.001"
                        min="0"
                        @keydown="onKeyDownPercent"
                        @input="formatPercent"
                    />
                    <span class="input-group-text bg-light border-start-0 text-secondary fw-500">% vs. mercado</span>
                </div>
                <small class="text-secondary size-12 mt-1 d-block">Formato decimal: 0.500 = 50%, 0.990 = 99%</small>
            </div>

            <!-- Competidores de referencia -->
            <div class="competitors-section">
                <label class="form-label fw-600 size-13 text-secondary mb-2">COMPETIDORES DE REFERENCIA</label>
                <div class="competitors-badges-list">
                    <span 
                        v-for="competitor in competitors" 
                        :key="competitor.id"
                        class="competitor-badge"
                        :class="{ 'competitor-badge--selected': isSelectedCompetitor(competitor.id) }"
                        @click="toggleCompetitor(competitor.id)"
                    >
                        {{ competitor.competitor_name }}
                    </span>
                </div>
            </div>
        </div>

        <div class="d-flex justify-content-end gap-2">
            <button class="btn btn-success fw-400 px-4" @click="saveCompetitorGroup" :disabled="saving">
                <span v-if="saving">Guardando...</span>
                <span v-else>Guardar</span>
            </button>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import configsRecGroupFiltersMixin from '../mixins/configsRecGroupFiltersMixin';

const POSITIONING_OPTIONS = [
    { value: 0, title: 'Por debajo del mercado', desc: 'Precio agresivo, ganar volumen', icon: 'arrow-down' },
    { value: 1, title: 'Paridad con mercado', desc: 'Alineado a la competencia', icon: 'balance-scale' },
    { value: 2, title: 'Por encima del mercado', desc: 'Premium / diferenciación', icon: 'arrow-up' },
    { value: 3, title: 'Precio fijo', desc: 'No se mueve por competidor', icon: 'lock' },
];

export default {
    name: 'config-competitor-group',
    mixins: [configsRecGroupFiltersMixin],
    props: {
        propHead: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            positioningOptions: POSITIONING_OPTIONS,
            positioning: 3,
            targetDifferential: 0.000,
            saving: false,
            competitors: [],
            selectedCompetitorIds: [],
        };
    },
    created() {
        this.getCompetitors();
    },
    methods: {
        ...mapActions(['store']),
        isSelectedCompetitor(id) {
            return this.selectedCompetitorIds.includes(id);
        },
        toggleCompetitor(id) {
            const index = this.selectedCompetitorIds.indexOf(id);
            if (index > -1) {
                this.selectedCompetitorIds.splice(index, 1);
            } else {
                this.selectedCompetitorIds.push(id);
            }
        },
        onKeyDownPercent(event) {
            if (event.key === '-') {
                event.preventDefault();
            }
        },
        formatPercent(event) {
            const rawValue = event.target.value;
            const sanitizedValue = String(rawValue).replace(/[^0-9.]/g, '');
            const parsedValue = parseFloat(sanitizedValue);

            if (!isNaN(parsedValue)) {
                let value = parsedValue;
                if (value > 1) {
                    value = value / 100;
                }
                this.targetDifferential = Number(value.toFixed(3));
            } else {
                this.targetDifferential = 0;
            }
        },
        saveCompetitorGroup() {
            this.saving = true;
            const data = {
                rule: this.positioning,
                value: (this.positioning === 1 || this.positioning === 3) ? 0 : this.targetDifferential,
                client_competitor: this.selectedCompetitorIds,
                business: this.authentication_user.client_business_select || null,
                product_group: this.localFilters.product_group || null,
                select_category: this.localFilters.select_category || null,
                brand: this.localFilters.brand || '',
                supplier: this.localFilters.supplier || '',
                product_status: this.localFilters.product_status || '',
                product_type: this.localFilters.product_type || '',
            };

            this.store({
                url: `/api/pricescrapingrules/update_group_polices/`,
                params: data,
            }).then(() => {
                this.saving = false;
                this.$emit('refrest');
                this.$notification.success('Configuración de competidor actualizada para los productos filtrados.', { timer: 5, showCloseIcn: true });
            }).catch((error) => {
                this.saving = false;
                if (error.status == 400) {
                    this.$notification.error(error.data.error || 'Error al guardar la configuración.', { timer: 5, showCloseIcn: true });
                } else {
                    this.$notification.error('Error al guardar la configuración.', { timer: 5, showCloseIcn: true });
                }
            });
        },
        getCompetitors() {
            let url = `/api/pricescrapingcompetitors/`;
            if (!this.authentication_user.client_channel) {
                url = url + `?business=${this.authentication_user.client_business_select}`;
            }
            this.get({ url }).then((response) => {
                this.competitors = response.results;
            });
        },
    }
};
</script>

<style scoped>
.config-competitor-card {
    background: #ffffff;
}

.filters-summary-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

.filters-summary-icon {
    color: #9ca3af;
    font-size: 0.85rem;
}

.filters-summary-title {
    font-size: 0.85rem;
    font-weight: 600;
    color: #6b7280;
}

.active-filters-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
}

.filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #f3f4f6;
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 0.8rem;
}

.filter-chip-label {
    color: #9ca3af;
    font-weight: 500;
}

.filter-chip-value {
    color: #374151;
    font-weight: 600;
}

.no-filters-msg {
    padding: 8px 0 16px;
}

.divider-line {
    height: 1px;
    background: #e5e7eb;
    margin-bottom: 20px;
}

.position-cards-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

.position-card {
    border: 2px solid #e5e7eb;
    border-radius: 10px;
    padding: 16px 12px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    background: #fff;
}

.position-card:hover {
    border-color: #1AA179;
}

.position-card--active {
    border-color: #1AA179;
    background: #e6f9f1;
}

.position-card-icon {
    font-size: 1.2rem;
    color: #6b7280;
    margin-bottom: 8px;
}

.position-card--active .position-card-icon {
    color: #1AA179;
}

.position-card-title {
    font-size: 0.85rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 4px;
}

.position-card-desc {
    font-size: 0.72rem;
    color: #9ca3af;
}

.input-group-text {
    background-color: #f8f9fa !important;
    border-color: #dee2e6 !important;
    font-size: 14px;
}

.form-control:focus {
    border-color: #1AA179;
    box-shadow: 0 0 0 0.2rem rgba(26, 161, 121, 0.25);
}

.competitors-section {
    margin-top: 8px;
}

.competitors-badges-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.competitor-badge {
    display: inline-block;
    background: #f3f4f6;
    color: #9ca3af;
    font-size: 0.82rem;
    font-weight: 600;
    padding: 6px 14px;
    border-radius: 999px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 2px solid transparent;
}

.competitor-badge:hover {
    border-color: #1AA179;
}

.competitor-badge--selected {
    background: #1AA179;
    color: #fff;
    border-color: #1AA179;
}

.btn-success {
    background-color: #1AA179 !important;
    border-color: #1AA179 !important;
}

.btn-success:hover:not(:disabled) {
    background-color: #158a65 !important;
    border-color: #158a65 !important;
}

.btn-success:disabled {
    opacity: 0.65;
}

.custom-select {
    border-radius: 6px;
    height: 40px;
    font-size: 14px;
}

.form-label {
    font-weight: 400;
    font-size: 16px;
    margin-bottom: 6px;
}
</style>
